import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Plus, Search, Eye, Building2, Calendar, LayoutGrid, RefreshCw,
  Filter, Settings, ChevronDown, List
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useNavigate } from 'react-router-dom';
import { Can } from '../../components/Can';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { DirectDebit, DirectDebitFilter } from '../../types/direct-debit';
import { Card, CardContent } from "../../components/ui/card";
import { financeApi } from '../../utils/finance-api';
import { toast } from 'sonner';

const DirectDebitListPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<DirectDebitFilter>({});
  const [directDebits, setDirectDebits] = useState<DirectDebit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDebits();
  }, [filter, searchQuery]);

  const loadDebits = async () => {
    setIsLoading(true);
    try {
      const res = await financeApi.listDirectDebits({ ...filter, q: searchQuery });
      setDirectDebits(res.data);
    } catch (error) {
      toast.error("Failed to load direct debit records");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = "AUD") => {
    const safeAmount = isNaN(amount) || amount === null || amount === undefined ? 0 : amount;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
    }).format(safeAmount);
  };

  const getStatusColor = (status: DirectDebit['status']) => {
    switch (status) {
      case "active": return "bg-emerald-50 text-emerald-700 border-emerald-100";
      case "suspended": return "bg-slate-50 text-slate-700 border-slate-100";
      case "cancelled": return "bg-rose-50 text-rose-700 border-rose-100";
      case "completed": return "bg-slate-50 text-slate-700 border-slate-100";
      default: return "bg-slate-50 text-slate-700 border-slate-100";
    }
  };

  return (
    <div className="space-y-6 container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Direct Debit Registry</h1>
          <p className="text-slate-500 text-sm mt-1">
            Comprehensive listing of all organizational liabilities and payment schedules
          </p>
        </div>
        <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 gap-2 border-slate-200 text-slate-600 hover:text-slate-900" onClick={() => navigate('/finance/direct-debit')}>
              <LayoutGrid className="h-4 w-4" />
              <span>Dashboard</span>
            </Button>
            <Can I={ACTIONS.CREATE} a={RESOURCES.DIRECT_DEBIT}>
              <Button size="sm" className="h-9 gap-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => navigate('/finance/direct-debit/new')}>
                <Plus className="h-4 w-4" />
                <span>New Direct Debit</span>
              </Button>
            </Can>
        </div>
      </div>
      
      <div className="bg-slate-100/50 rounded-xl p-4 space-y-4">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input 
            placeholder="Search debits, payees, reference..." 
            className="pl-9 bg-white border-slate-200 focus-visible:ring-slate-200"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={filter.frequency || "all"} onValueChange={(val) => setFilter(f => ({ ...f, frequency: val as any }))}>
            <SelectTrigger className="bg-white border-slate-200 w-full md:w-48 h-10 px-3 font-normal text-slate-700">
              <div className="flex items-center gap-2">
                 <List size={14} className="text-slate-400" />
                 <SelectValue placeholder="All Frequencies" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Frequencies</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annually">Annually</SelectItem>
              <SelectItem value="ad-hoc">Ad-hoc</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.status || "all"} onValueChange={(val) => setFilter(f => ({ ...f, status: val as any }))}>
            <SelectTrigger className="bg-white border-slate-200 w-full md:w-48 h-10 px-3 font-normal text-slate-700">
              <div className="flex items-center gap-2">
                 <Filter size={14} className="text-slate-400" />
                 <SelectValue placeholder="All Status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Inactive</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          
          {(filter.frequency || filter.status || searchQuery) && (
            <Button variant="ghost" size="sm" className="text-slate-500 h-10" onClick={() => { setFilter({}); setSearchQuery(""); }}>
              Reset Filters
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-100 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-transparent hover:bg-transparent border-slate-100">
                <TableHead className="font-normal py-3 text-slate-900 underline underline-offset-4 decoration-slate-200">Description & Entity</TableHead>
                <TableHead className="font-normal py-3 text-slate-900">Payee</TableHead>
                <TableHead className="font-normal py-3 text-slate-900 text-right">Amount</TableHead>
                <TableHead className="font-normal py-3 text-slate-900 text-center">Next Date</TableHead>
                <TableHead className="font-normal py-3 text-slate-900">Status</TableHead>
                <TableHead className="text-right font-normal py-3 text-slate-900">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-64 text-center py-20">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-200" />
                  </TableCell>
                </TableRow>
              ) : directDebits.length > 0 ? (
                directDebits.map((debit) => (
                  <TableRow key={debit.id} className="hover:bg-slate-50/80 cursor-pointer border-slate-100 transition-colors" onClick={() => navigate(`/finance/direct-debit/${debit.id}`)}>
                    <TableCell className="py-4">
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 bg-slate-100 rounded flex items-center justify-center shrink-0 mt-0.5"><Building2 className="h-4 w-4 text-slate-500" /></div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{debit.description}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{debit.company?.name || 'Unknown'} • {debit.department}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-medium text-slate-700">{debit.payeeName}</TableCell>
                    <TableCell className="text-right text-sm font-bold text-slate-900">
                      {formatCurrency(debit.monthlyDebitAmount, debit.currency)}
                    </TableCell>
                    <TableCell className="text-center text-xs font-semibold text-slate-500 uppercase tracking-tighter">
                      {debit.status === 'active' && debit.nextScheduledDate ? new Date(debit.nextScheduledDate).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`px-2 py-0 h-6 text-[10px] font-bold uppercase tracking-wider rounded-full border-0 ${getStatusColor(debit.status)}`}>
                        {debit.status === 'suspended' ? 'inactive' : debit.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-900" onClick={(e) => { e.stopPropagation(); navigate(`/finance/direct-debit/${debit.id}`); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-64 text-center py-20 text-slate-400 italic">
                    <div className="flex flex-col items-center justify-center opacity-50">
                      <CreditCard className="h-12 w-12 mb-3" />
                      <p>No records found matching your selection.</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DirectDebitListPage;
